import * as React from "react";
import { Button, Spinner, Text, makeStyles } from "@fluentui/react-components";
import { DeleteDismiss24Regular, LinkAdd24Regular } from "@fluentui/react-icons";
import type {
  QueryAttributeDefinition,
  ConditionDraft,
  ConditionGroupDraft,
  QueryEntityMetadata,
  QueryRelationDefinition,
  JoinDraft,
  QueryTarget,
} from "../../../types.js";
import { EntityQueryNodeControl } from "../EntityQueryNodeControl.js";
import { FilterPopoverControl } from "../../popovers/FilterPopoverControl.js";
import { InfoPopoverControl } from "../../popovers/InfoPopoverControl.js";
import { JoinSettingsPopoverControl } from "../../popovers/JoinSettingsPopoverControl.js";
import { RelationRowControl } from "../RelationRowControl.js";
import { SelectionPopoverControl } from "../../popovers/SelectionPopoverControl.js";
import { SectionHeaderControl } from "../SectionHeaderControl.js";
import { TargetFiltersEditorControl } from "../TargetFiltersEditorControl.js";
import { LINK_TYPES, getParentMetadataForJoin, getTargetLabel } from "../fetchXmlBuilderHelpers.js";

const useStyles = makeStyles({
  container: {
    display: "grid",
    gap: "8px",
  },
  childWrap: {
    marginLeft: "10px",
    borderLeft: "2px solid var(--colorNeutralStroke2)",
    paddingLeft: "10px",
    display: "grid",
    gap: "8px",
  },
  relatedGroup: {
    display: "grid",
    gap: "8px",
  },
  lookupGroup: {
    display: "grid",
    gap: "8px",
  },
  warningRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    color: "var(--colorPaletteDarkOrangeForeground1)",
  },
});

export interface JoinsSectionProps {
  rootMetadata: QueryEntityMetadata;
  rootSelectedAttributes: string[];
  joins: JoinDraft[];
  joinMetadataByJoinId: Record<string, QueryEntityMetadata | undefined>;
  conditionGroups: ConditionGroupDraft[];
  conditions: ConditionDraft[];
  showFilters: boolean;
  onToggleRootAttribute: (attributeName: string, checked: boolean) => void;
  onToggleShowFilters: (show: boolean) => void;
  onAddJoin: (parentTarget: QueryTarget, relationKey?: string) => void;
  onRemoveJoin: (joinId: string) => void;
  onChangeJoin: (joinId: string, update: Partial<JoinDraft>) => void;
  onToggleJoinAttribute: (joinId: string, attributeName: string, checked: boolean) => void;
  onAddTopLevelGroup: (target: QueryTarget) => void;
  onAddCondition: (groupId: string) => void;
  onAddSubGroup: (groupId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  onChangeGroup: (groupId: string, update: Partial<ConditionGroupDraft>) => void;
  onChangeCondition: (conditionId: string, update: Partial<ConditionDraft>) => void;
  onRemoveCondition: (conditionId: string) => void;
  joinValidationMessageById?: Record<string, string>;
  onDismissJoinValidationMessage?: (joinId: string) => void;
}

export function JoinsSection({
  rootMetadata,
  rootSelectedAttributes,
  joins,
  joinMetadataByJoinId,
  conditionGroups,
  conditions,
  showFilters,
  onToggleRootAttribute,
  onToggleShowFilters,
  onAddJoin,
  onRemoveJoin,
  onChangeJoin,
  onToggleJoinAttribute,
  onAddTopLevelGroup,
  onAddCondition,
  onAddSubGroup,
  onRemoveGroup,
  onChangeGroup,
  onChangeCondition,
  onRemoveCondition,
  joinValidationMessageById,
  onDismissJoinValidationMessage,
}: JoinsSectionProps): JSX.Element {
  const styles = useStyles();
  const hasActiveRootFilters = conditions.some((condition) => condition.target === "root");

  const renderEntityNode = (params: {
    nodeKey: string;
    titleLeading?: React.ReactNode;
    title: React.ReactNode;
    titleActions?: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    beforeAttributes?: React.ReactNode;
    attributes: QueryAttributeDefinition[];
    selectedAttributes: string[];
    onToggleAttribute: (attributeName: string, checked: boolean) => void;
    attributePlaceholder: string;
    showQuickRemoveAttributes?: boolean;
    showAttributes?: boolean;
    collapsible?: boolean;
    children?: React.ReactNode;
  }): JSX.Element => {
    const {
      nodeKey,
      titleLeading,
      title,
      titleActions,
      subtitle,
      actions,
      beforeAttributes,
      attributes,
      selectedAttributes,
      onToggleAttribute,
      attributePlaceholder,
      showQuickRemoveAttributes,
      showAttributes,
      collapsible,
      children,
    } = params;

    return (
      <EntityQueryNodeControl
        key={nodeKey}
        titleLeading={titleLeading}
        title={title}
        titleActions={titleActions}
        subtitle={subtitle}
        actions={actions}
        beforeAttributes={beforeAttributes}
        attributes={attributes}
        selectedAttributes={selectedAttributes}
        onToggleAttribute={onToggleAttribute}
        attributePlaceholder={attributePlaceholder}
        showQuickRemoveAttributes={showQuickRemoveAttributes}
        showAttributes={showAttributes}
        collapsible={collapsible}
      >
        {children}
      </EntityQueryNodeControl>
    );
  };

  const renderTargetFilters = (target: QueryTarget, title: string, hasActiveFilters: boolean): JSX.Element | null => {
    if (!showFilters) {
      return null;
    }

    return (
      <FilterPopoverControl buttonLabel="Filters" hasActiveFilters={hasActiveFilters}>
        <TargetFiltersEditorControl
          target={target}
          title={title}
          conditionGroups={conditionGroups}
          conditions={conditions}
          rootMetadata={rootMetadata}
          joinMetadataByJoinId={joinMetadataByJoinId}
          onAddTopLevelGroup={onAddTopLevelGroup}
          onAddCondition={onAddCondition}
          onAddSubGroup={onAddSubGroup}
          onRemoveGroup={onRemoveGroup}
          onChangeGroup={onChangeGroup}
          onChangeCondition={onChangeCondition}
          onRemoveCondition={onRemoveCondition}
        />
      </FilterPopoverControl>
    );
  };

  const getOneToManyRelationOptions = (parentTarget: QueryTarget, metadata: QueryEntityMetadata) =>
    metadata.relations
      .filter((relation) => relation.relationType === "one-to-many" || relation.relationType === "many-to-many")
      .map((relation) => ({
        key: relation.key,
        label: relation.label,
        selected: joins.some((join) => join.parentTarget === parentTarget && join.relationKey === relation.key),
      }));

  const getRelationLabel = (parentMetadata: QueryEntityMetadata, relationKey: string): string => {
    const relation = parentMetadata.relations.find((item) => item.key === relationKey);
    return relation?.label || relationKey || "(unconfigured relation)";
  };

  const getRelationByJoin = (parentMetadata: QueryEntityMetadata, join: JoinDraft): QueryRelationDefinition | undefined => {
    if (join.relationKey) {
      const byKey = parentMetadata.relations.find((item) => item.key === join.relationKey);
      if (byKey) {
        return byKey;
      }
    }

    return parentMetadata.relations.find(
      (item) =>
        item.targetEntityLogicalName === join.targetEntityLogicalName &&
        item.fromAttribute === join.fromAttribute &&
        item.toAttribute === join.toAttribute,
    );
  };

  const getRelationLabelForJoin = (parentMetadata: QueryEntityMetadata, join: JoinDraft): string => {
    const relation = getRelationByJoin(parentMetadata, join);
    if (relation) {
      return relation.label;
    }

    if (join.relationKey) {
      return join.relationKey;
    }

    if (join.targetEntityLogicalName) {
      return `${join.targetEntityLogicalName} (${join.fromAttribute || "?"} -> ${join.toAttribute || "?"})`;
    }

    return "(unconfigured relation)";
  };

  const getChildJoinRelationType = (parentMetadata: QueryEntityMetadata, join: JoinDraft): QueryRelationDefinition["relationType"] | null => {
    const relation = getRelationByJoin(parentMetadata, join);
    return relation?.relationType || null;
  };

  const renderRelationFilters = (join: JoinDraft, relationLabel: string, hasActiveFilters: boolean): JSX.Element | null => {
    return renderTargetFilters(join.id, `${relationLabel} filters`, hasActiveFilters);
  };

  const renderRelatedEntities = (
    parentTarget: QueryTarget,
    parentMetadata: QueryEntityMetadata,
    ancestry: Set<string>,
  ): JSX.Element => {
    const childJoins = joins.filter(
      (item) =>
        item.parentTarget === parentTarget &&
        !ancestry.has(item.id) &&
        ["one-to-many", "many-to-many"].includes(getChildJoinRelationType(parentMetadata, item) || ""),
    );
    const relationOptions = getOneToManyRelationOptions(parentTarget, parentMetadata);

    return (
      <div className={styles.relatedGroup}>
        <SectionHeaderControl
          title="Related entities"
          titleActions={
            <SelectionPopoverControl
              buttonLabel=""
              buttonIcon={<LinkAdd24Regular />}
              buttonAriaLabel={parentTarget === "root" ? "Manage root relations" : "Manage related entities"}
              title={`Related entities from ${parentMetadata.displayName}`}
              options={relationOptions}
              multiselect
              buttonAppearance="subtle"
              onToggleOption={(relationKey, checked) => {
                const existingJoin = joins.find(
                  (join) => join.parentTarget === parentTarget && join.relationKey === relationKey,
                );

                if (checked && !existingJoin) {
                  onAddJoin(parentTarget, relationKey);
                }

                if (!checked && existingJoin) {
                  onRemoveJoin(existingJoin.id);
                }
              }}
            />
          }
        />

        {childJoins.length === 0 ? <Text>No related entities added.</Text> : null}

        {childJoins.map((childJoin) => {
          const relationLabel = getRelationLabelForJoin(parentMetadata, childJoin);
          const hasActiveFilters = conditions.some((condition) => condition.target === childJoin.id);

          return (
            <RelationRowControl
              key={childJoin.id}
              label={relationLabel}
              actions={
                <>
                  <Button
                    appearance="subtle"
                    icon={<DeleteDismiss24Regular />}
                    aria-label={`Remove relation ${relationLabel}`}
                    title="Remove relation"
                    onClick={() => onRemoveJoin(childJoin.id)}
                  />
                  {renderRelationFilters(childJoin, relationLabel, hasActiveFilters)}
                </>
              }
            >
              <div className={styles.childWrap}>{renderJoinNode(childJoin, new Set([...ancestry, childJoin.id]))}</div>
            </RelationRowControl>
          );
        })}
      </div>
    );
  };

  const renderLookupEntities = (
    parentTarget: QueryTarget,
    parentMetadata: QueryEntityMetadata,
    ancestry: Set<string>,
  ): JSX.Element | null => {
    const lookupJoins = joins.filter(
      (item) =>
        item.parentTarget === parentTarget &&
        !ancestry.has(item.id) &&
        getChildJoinRelationType(parentMetadata, item) === "many-to-one",
    );

    if (lookupJoins.length < 1) {
      return null;
    }

    return (
      <div className={styles.lookupGroup}>
        <SectionHeaderControl title="Lookup-linked entities" />
        {lookupJoins.map((lookupJoin) => {
          const relationLabel = getRelationLabelForJoin(parentMetadata, lookupJoin);
          const hasActiveFilters = conditions.some((condition) => condition.target === lookupJoin.id);

          return (
            <RelationRowControl
              key={lookupJoin.id}
              label={relationLabel}
              actions={renderRelationFilters(lookupJoin, relationLabel, hasActiveFilters)}
            >
              <div className={styles.childWrap}>{renderJoinNode(lookupJoin, new Set([...ancestry, lookupJoin.id]))}</div>
            </RelationRowControl>
          );
        })}
      </div>
    );
  };

  const renderJoinNode = (join: JoinDraft, ancestry: Set<string>): JSX.Element => {
    const parentMetadata = getParentMetadataForJoin(join, rootMetadata, joinMetadataByJoinId);
    const relationOptions = (parentMetadata?.relations || []).map((item) => [item.key, item.label] as [string, string]);
    const joinMetadata = joinMetadataByJoinId[join.id];
    const relation = parentMetadata ? getRelationByJoin(parentMetadata, join) : undefined;
    const validationMessage = joinValidationMessageById?.[join.id];
    const showQuickRemoveAttributes = true;

    return renderEntityNode({
      nodeKey: join.id,
      titleLeading: (
        <InfoPopoverControl title="Entity relationship info">
          <Text>Join: {join.targetEntityLogicalName || "(not resolved yet)"}</Text>
          <Text>Alias: {join.alias || "(auto)"}</Text>
          <Text>
            Relation: {relation ? getRelationLabel(parentMetadata || rootMetadata, relation.key) : "(not selected)"}
          </Text>
          <Text>
            Parent: {getTargetLabel(join.parentTarget, rootMetadata, joins)} | {relation?.relationType || "relation"} | {join.fromAttribute || "?"} -&gt; {join.toAttribute || "?"}
          </Text>
        </InfoPopoverControl>
      ),
      title: join.targetEntityLogicalName || "Entity",
      titleActions: (
        <JoinSettingsPopoverControl
          alias={join.alias}
          linkType={join.linkType}
          linkTypeOptions={LINK_TYPES}
          relationOptions={relationOptions}
          relationKey={join.relationKey}
          onAliasChange={(value) => onChangeJoin(join.id, { alias: value })}
          onLinkTypeChange={(value) => onChangeJoin(join.id, { linkType: value })}
          onRelationChange={(value) => onChangeJoin(join.id, { relationKey: value })}
        />
      ),
      collapsible: true,
      beforeAttributes: (
        <>
          {validationMessage ? (
            <div className={styles.warningRow}>
              <Text>{validationMessage}</Text>
              {onDismissJoinValidationMessage ? (
                <Button appearance="subtle" onClick={() => onDismissJoinValidationMessage(join.id)}>
                  Dismiss
                </Button>
              ) : null}
            </div>
          ) : null}

          {join.targetEntityLogicalName && !joinMetadata ? <Spinner label="Loading joined entity metadata" /> : null}
        </>
      ),
      attributes: joinMetadata?.attributes || [],
      selectedAttributes: join.selectedAttributes,
      onToggleAttribute: (attributeName, checked) => onToggleJoinAttribute(join.id, attributeName, checked),
      attributePlaceholder: "Select joined attributes",
      showQuickRemoveAttributes,
      showAttributes: Boolean(joinMetadata),
      children: joinMetadata ? (
        <>
          {renderLookupEntities(join.id, joinMetadata, new Set([...ancestry, join.id]))}
          {renderRelatedEntities(join.id, joinMetadata, new Set([...ancestry, join.id]))}
        </>
      ) : null,
    });
  };

  const renderRootNode = (): JSX.Element =>
    renderEntityNode({
      nodeKey: "root",
      title: `${rootMetadata.displayName} attributes (Root entity)`,
      actions: (
        <>
          <Button appearance="subtle" onClick={() => onToggleShowFilters(!showFilters)}>
            {showFilters ? "Hide filters" : "Show filters"}
          </Button>
          {renderTargetFilters("root", "Root filters", hasActiveRootFilters)}
        </>
      ),
      attributes: rootMetadata.attributes,
      selectedAttributes: rootSelectedAttributes,
      onToggleAttribute: onToggleRootAttribute,
      attributePlaceholder: "Select root attributes",
      showQuickRemoveAttributes: true,
      children: (
        <>
          {renderLookupEntities("root", rootMetadata, new Set())}
          {renderRelatedEntities("root", rootMetadata, new Set())}
        </>
      ),
    });

  return <div className={styles.container}>{renderRootNode()}</div>;
}
